/**
 * @fileoverview Places a Google Identity Services button in its container,
 * scaled up to the container's height and faded in once its iframe has loaded,
 * so the box does not pop from empty to drawn.
 *
 * GSI draws the button in an iframe at a fixed height and its look cannot be
 * styled from outside, so a CSS scale is the only way to make it taller.
 */

/** Height of a GSI "large" button, the size that gets scaled. */
export const GSI_BUTTON_HEIGHT_PX = 40;
/** GSI refuses widths above this. */
const GSI_MAX_WIDTH_PX = 400;
/** Used while the container is hidden and has no size of its own. */
const FALLBACK_WIDTH_PX = 320;
const FALLBACK_HEIGHT_PX = 48;
export const FADE_MS = 300;
/** Show the button even if its iframe never reports `load`. */
export const LOAD_TIMEOUT_MS = 1500;

/**
 * Renders the button into `parent`, scaled to fill its height and full width.
 *
 * @param parent A `position: relative` container with a fixed height.
 * @param render Draws the GSI button into `frame` at `width` (unscaled px).
 */
export function mountScaledButton(
  parent: HTMLElement,
  render: (frame: HTMLElement, width: number) => void,
): void {
  const scale = Math.max(1, (parent.clientHeight || FALLBACK_HEIGHT_PX) / GSI_BUTTON_HEIGHT_PX);
  // GSI rejects a fractional width and falls back to its narrow default.
  const width = Math.floor(
    Math.min((parent.clientWidth || FALLBACK_WIDTH_PX) / scale, GSI_MAX_WIDTH_PX),
  );

  const frame = document.createElement('div');
  Object.assign(frame.style, {
    position: 'absolute',
    top: '0',
    left: '50%',
    width: `${width}px`,
    height: `${GSI_BUTTON_HEIGHT_PX}px`,
    transform: `translateX(-50%) scale(${scale})`,
    transformOrigin: 'top center',
    opacity: '0',
    transition: `opacity ${FADE_MS}ms ease`,
  });
  parent.replaceChildren(frame);
  render(frame, width);

  let shown = false;
  const show = () => {
    if (shown) return;
    shown = true;
    stopWatching();
    frame.style.opacity = '1';
  };
  const stopWatching = onIframeLoad(frame, show);
  setTimeout(show, LOAD_TIMEOUT_MS);
}

/** Calls `onLoad` when an iframe inside `frame` loads, including one added later. */
function onIframeLoad(frame: HTMLElement, onLoad: () => void): () => void {
  const attach = (iframe: HTMLIFrameElement) =>
    iframe.addEventListener('load', onLoad, { once: true });

  const existing = frame.querySelector('iframe');
  if (existing) {
    attach(existing);
    return () => {};
  }
  const observer = new MutationObserver(() => {
    const iframe = frame.querySelector('iframe');
    if (!iframe) return;
    observer.disconnect();
    attach(iframe);
  });
  observer.observe(frame, { childList: true, subtree: true });
  return () => observer.disconnect();
}
